/**
 * FeatureGate Component
 *
 * Conditionally renders children based on feature access.
 * Shows upgrade prompt when feature is locked.
 */

import React from "react";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import { FeatureKey } from "../../types/featureAccess";
import UpgradePrompt from "./UpgradePrompt";

interface FeatureGateProps {
  /** The feature key to check access for */
  feature: FeatureKey;
  /** Content to render when feature is unlocked */
  children: React.ReactNode;
  /** Optional custom fallback when feature is locked */
  fallback?: React.ReactNode;
  /** Whether to show the upgrade prompt (default: true) */
  showUpgradePrompt?: boolean;
  /** Whether to hide content completely when locked (default: false) */
  hideCompletely?: boolean;
}

export const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  children,
  fallback,
  showUpgradePrompt = true,
  hideCompletely = false,
}) => {
  const { hasFeature, getRequiredTier, access, isLoading } = useFeatureAccess();

  // Still loading - show nothing
  if (isLoading || !access) {
    return null;
  }

  // Feature is unlocked - render children
  if (hasFeature(feature)) {
    return <>{children}</>;
  }

  // Feature is locked
  if (hideCompletely) {
    return null;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  if (showUpgradePrompt) {
    return (
      <UpgradePrompt
        requiredTier={getRequiredTier(feature)}
        feature={feature}
        currentTier={access.subscription_tier}
      />
    );
  }

  return null;
};

export default FeatureGate;
